// -----------------------------------------------------------------------
// <copyright company="Microsoft Corporation">
// Copyright (c) Microsoft Corporation. All rights reserved.
// </copyright>
// -----------------------------------------------------------------------

import { OnePlayerError } from '../common/onePlayerError';
import { OnePlayerLoaderBase } from '../common/onePlayerLoaderBase';
import { iframeLoaderVersion } from '../loaderVersion';
import type {
  IEmbedLoaderOptions,
  IEmbedPlaybackSessionOptions,
  LoadMode,
  RelativeTimestamp
} from '../types/external';
import type { IExperimentationEmbedParameter, INavEmbedParameter } from '../types/internal';

export class GenericFileEmbedLoader extends OnePlayerLoaderBase {
  public constructor(options: IEmbedLoaderOptions) {
    super(options);
  }

  protected override adjustEmbedUrlQueryString(
    loadMode: LoadMode,
    embedUrl: string,
    playbackSessionOptions: IEmbedPlaybackSessionOptions,
    createSessionTimestamp: RelativeTimestamp
  ): string {
    const url = new URL(embedUrl);
    url.searchParams.delete('access_token');
    url.searchParams.set('auth', 'readwrite');

    // Calculate theme override parameter
    let ust: number | undefined = undefined;
    let pc: string | undefined = undefined;
    let theme: string | undefined = undefined;
    if (playbackSessionOptions.theme) {
      ust = playbackSessionOptions.theme.name === 'light' ? 1 : 0;
      pc = playbackSessionOptions.theme.primaryColor;
      theme = playbackSessionOptions.theme.fluentTheme;
    }

    const embedOptions = JSON.stringify({
      ap: loadMode === 'autoplayOn' ? 1 : 0,
      dl: loadMode === 'deferredLoad' ? 1 : undefined,
      ha: playbackSessionOptions.hostApp,
      hp: playbackSessionOptions.hostPlatform,
      hv: playbackSessionOptions.hostView,
      hc: playbackSessionOptions.hostComponent,
      hci: playbackSessionOptions.hostCorrelationId,
      het: playbackSessionOptions.hostExtraTelemetry,
      hra: playbackSessionOptions.hostReferrerApp,
      hrp: playbackSessionOptions.hostReferrerAppPlatform,
      hrv: playbackSessionOptions.hostReferrerView,
      hrm: playbackSessionOptions.hostReferrerMode,
      hrps: playbackSessionOptions.hostReferrerPlaybackSessionId,
      hvn: playbackSessionOptions.hostVersion,
      he: playbackSessionOptions.hostEnvironment,
      hm: playbackSessionOptions.hostMode,
      hsi: playbackSessionOptions.hostSessionId,
      hr: playbackSessionOptions.hostReferrer,
      hrs: playbackSessionOptions.hostReferrerScenario,
      lv: iframeLoaderVersion,
      t0: playbackSessionOptions.hostTimestamps.t0.timestamp ?? 0,
      t0to: playbackSessionOptions.hostTimestamps.t0.timeOrigin ?? performance.timeOrigin,
      tcs: createSessionTimestamp.timestamp,
      tcsto: createSessionTimestamp.timeOrigin,
      ust,
      pc,
      theme,
      ol: playbackSessionOptions.overrideLanguage,
      hvm: playbackSessionOptions.hideVideoMetadata,
      itf: playbackSessionOptions.enableVideoPlayerTabstopDuringPlayback ? 'rc' : 'ffe'
    });
    url.searchParams.set('embed', embedOptions);

    if (playbackSessionOptions.startTimeInSeconds) {
      const nav: INavEmbedParameter = {
        playbackOptions: {
          startTimeInSeconds: playbackSessionOptions.startTimeInSeconds
        }
      };
      url.searchParams.set('nav', JSON.stringify(nav));
    }

    if (playbackSessionOptions.flightsUsed || playbackSessionOptions.flightsUnused) {
      const experimentation: IExperimentationEmbedParameter = {
        flightsUsed: playbackSessionOptions.flightsUsed
          ? playbackSessionOptions.flightsUsed.join(',')
          : undefined,
        flightsUnused: playbackSessionOptions.flightsUnused
          ? playbackSessionOptions.flightsUnused.join(',')
          : undefined
      };

      url.searchParams.set('exp', JSON.stringify(experimentation));
    }

    return url.toString();
  }

  protected override getEmbedUrlAsync(videoUrl: string): Promise<string> {
    if (videoUrl.indexOf('embed.aspx') !== -1) {
      return Promise.resolve(videoUrl);
    }

    // TODO PBI 1488177: support "copy link" and "share link" URLs, possibly by calling the Graph API
    throw OnePlayerError.createGenericError('fatal', 'loadIframe', 'unrecognizedVideoUrlFormat', undefined);
  }
}
